/**
 * Обвязка над Telegram Mini App SDK: initData для авторизации в нашем API,
 * тема, кнопка «назад», хаптика.
 *
 * Вне Telegram (обычный браузер при разработке) SDK недоступен — тогда берём
 * initData из `VITE_DEV_INIT_DATA`, чтобы экраны можно было гонять локально.
 */
import { retrieveRawInitData } from '@telegram-apps/sdk';

export interface TelegramEnv {
  initData: string;
  /** Внутри Telegram или в обычном браузере. */
  inTelegram: boolean;
  colorScheme: 'dark' | 'light';
  /** Язык интерфейса Telegram (`en`, `ru`, …) — нужен до загрузки словаря. */
  languageCode: string | null;
}

interface WebApp {
  initData?: string;
  initDataUnsafe?: { user?: { language_code?: string } };
  colorScheme?: 'dark' | 'light';
  ready?(): void;
  expand?(): void;
  openLink?(url: string): void;
  openTelegramLink?(url: string): void;
  HapticFeedback?: { impactOccurred(style: string): void };
}

function webApp(): WebApp | undefined {
  return (globalThis as { Telegram?: { WebApp?: WebApp } }).Telegram?.WebApp;
}

/**
 * initData из адресной строки — запасной путь, когда официальный скрипт не
 * успел инициализироваться. Telegram кладёт подпись в `tgWebAppData`
 * (обычно в hash, иногда в query — проверяем оба).
 */
function initDataFromLocation(): string {
  for (const source of [globalThis.location?.hash ?? '', globalThis.location?.search ?? '']) {
    const params = new URLSearchParams(source.replace(/^[#?]/, ''));
    const raw = params.get('tgWebAppData');
    if (raw && raw.length > 0) return raw;
  }
  return '';
}

let cached: TelegramEnv | null = null;

export function telegramEnv(): TelegramEnv {
  if (cached) return cached;

  const app = webApp();

  /**
   * Три независимых источника подписи — в порядке надёжности.
   *
   * `retrieveLaunchParams().initDataRaw` из старого кода не работал: в SDK 3.x
   * это поле переименовано, и `initData` всегда оказывался пустым — Mini App
   * получал 401 на каждый запрос. Поэтому SDK здесь только один из вариантов,
   * а не единственный.
   */
  const candidates: Array<() => string> = [
    () => retrieveRawInitData() ?? '',
    () => app?.initData ?? '',
    initDataFromLocation,
  ];

  for (const read of candidates) {
    let raw = '';
    try {
      raw = read();
    } catch {
      continue;
    }

    if (raw.length > 0) {
      cached = {
        initData: raw,
        inTelegram: true,
        colorScheme: app?.colorScheme ?? 'dark',
        languageCode: app?.initDataUnsafe?.user?.language_code ?? null,
      };
      return cached;
    }
  }

  cached = {
    // dev-подпись доступна только в режиме разработки: в собранный бандл
    // она попасть не должна (инцидент 2026-08-20 — см. PROGRESS)
    initData: import.meta.env.DEV ? (import.meta.env['VITE_DEV_INIT_DATA'] ?? '') : '',
    inTelegram: false,
    colorScheme: 'dark',
    languageCode: null,
  };
  return cached;
}

/** Тактильный отклик на нажатие; вне Telegram — тихо ничего не делает. */
export function haptic(style: 'light' | 'medium' | 'rigid' = 'light'): void {
  webApp()?.HapticFeedback?.impactOccurred(style);
}

/** Разворачивает окно на всю высоту — иначе Mini App открывается «половинкой». */
export function expandViewport(): void {
  const app = webApp();
  app?.ready?.();
  app?.expand?.();
}

/** Открыть внешнюю ссылку (депозит/вывод у брокера) правильным способом. */
export function openLink(url: string): void {
  const app = webApp();
  if (app?.openLink) app.openLink(url);
  else globalThis.open(url, '_blank', 'noopener');
}

/**
 * Открыть t.me-ссылку (сапорт, канал) внутри Telegram — в отличие от
 * `openLink`, чат открывается поверх, а сама прила НЕ закрывается.
 */
export function openTelegramLink(url: string): void {
  const app = webApp();
  if (app?.openTelegramLink) app.openTelegramLink(url);
  else openLink(url);
}
